import { useQuery } from '@apollo/client';
import { IconButton, SxProps, Theme } from '@mui/material';
import { getFullName } from '@provision/formatting-utils';
import { useCallback, useMemo } from 'react';
import { DynamicIcon, Tooltip } from 'src/components';
import { SelectMenu, type SelectMenuOption } from 'src/components/SelectMenu';
import { gql } from 'src/gql';
import {
  Address,
  DEFAULT_CLIENTS_QUERY_LIMIT,
  PartnerGroupRules,
  usePartnerGroupRules,
} from 'src/graphql';
import { useActivationFlowUrl } from 'src/graphql/entities/activation-flow-url';
import { useCopyToClipboard } from 'src/utils/hooks';
import { toSxArray } from 'src/utils/sx';
import { Platform } from 'src/utils/system';

const SiteShareClientsQuery = gql(`
  query SiteShareClientsQuery($params: QueryParamsWithSearch) @entity(name: "Client") {
    getClient(queryParams: $params) {
      records {
        id
        clientId
        firstName
        lastName
        primaryEmail {
          id
          value
        }
      }
    }
  }
`);

interface ClientOption {
  id: string;
  value: string;
  firstName: string;
  lastName: string;
  email: string;
}

interface ShareSiteButtonProps {
  address: Address;
  partnerId: number;
  sx?: SxProps<Theme>;
}

export const SiteShareButton = ({ address, partnerId, sx }: ShareSiteButtonProps) => {
  const { generateAfMagicLoginUrl } = useActivationFlowUrl();

  const { partnerGroupRules } = usePartnerGroupRules(partnerId);

  const { copyToClipboard } = useCopyToClipboard();

  const filters = useMemo(
    () =>
      address?.addressId
        ? [
            {
              attribute: 'addresses/addressId',
              operator: '=',
              value: address?.addressId || '',
            },
          ]
        : [],
    [address?.addressId],
  );

  const { data } = useQuery(SiteShareClientsQuery, {
    variables: {
      params: {
        filters,
        limit: DEFAULT_CLIENTS_QUERY_LIMIT,
      },
    },
    skip: !filters.length,
  });

  const clientOptions = useMemo(() => {
    return (data?.getClient?.records ?? [])
      .filter((client) => !!client)
      .map((client) => ({
        id: client.clientId,
        value: client.clientId,
        firstName: client.firstName ?? '',
        lastName: client.lastName ?? '',
        email: client.primaryEmail?.value ?? '',
      }));
  }, [data]);

  const menuOptions = useMemo<SelectMenuOption[]>(
    () =>
      clientOptions.map((option) => ({
        value: option.value,
        title: getFullName(option),
        description: option.email || 'Unavailable. No email address.',
        icon: <DynamicIcon name="link" sx={{ transform: 'rotate(135deg)' }} />,
        disabled: !option.email,
      })),
    [clientOptions],
  );

  const handleCopyActivationFlowUrl = useCallback(
    async (option: ClientOption | undefined) => {
      if (!option || !address?.addressId) return;

      const url = await generateAfMagicLoginUrl({
        addressId: address.addressId,
        clientId: option.value,
        partnerId,
        email: option.email,
        disableLink: true,
      });

      if (url?.url) {
        await copyToClipboard(url.url, { openInModal: Platform.isSafari });
      }
    },
    [address?.addressId, copyToClipboard, generateAfMagicLoginUrl, partnerId],
  );

  return partnerGroupRules[PartnerGroupRules.AccessToSubscriptionManagement] ? (
    <SelectMenu
      label="Choose a link to copy"
      options={menuOptions}
      value={null}
      searchPlaceholder="Select client to copy URL"
      menuWidth="20rem"
      onChange={(clientId) => {
        void handleCopyActivationFlowUrl(
          clientOptions.find((option) => option.value === clientId),
        );
      }}
      renderTrigger={({ getTriggerProps }) => (
        <Tooltip
          title={
            <div>
              Generate Client Activation URL
              <br />
              Click and select a user to
              <br />
              generate a unique URL.
            </div>
          }
        >
          <IconButton
            {...getTriggerProps()}
            size="small"
            sx={[{ padding: 0 }, ...toSxArray(sx)]}
            data-testid="site-share-button"
          >
            <DynamicIcon name="share-2" library="lucide" />
          </IconButton>
        </Tooltip>
      )}
    />
  ) : null;
};
